import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { AvoirDraftBanner } from "@/components/documents/AvoirNotes";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { documentAPI, adminAPI } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, FileText, ChevronRight, AlertCircle } from "lucide-react";
import { DocumentItemsEditor } from "@/components/document-form/DocumentItemsEditor";
import LiveDocumentLinesPreview from "@/components/document-form/LiveDocumentLinesPreview";
import { DocumentTotals } from "@/components/document-form/DocumentTotals";
import { PaymentDetailsSection } from "@/components/document-form/PaymentDetailsSection";
import { DeliveryAddressSection } from "@/components/document-form/DeliveryAddressSection";
import { NumberField } from "@/components/document-form/NumberField";
import { DateField } from "@/components/ui/date-field";
import { AvoirModalityField, defaultAvoirModality } from "@/components/document-form/AvoirModalityField";

const STATUS_MAP = {
  contrat: ["brouillon", "actif", "expire", "resilie"],
  devis: ["brouillon", "envoye", "accepte", "acompté", "soldé", "refuse", "expire"],
  proforma: ["brouillon", "envoye", "accepte"],
  commande: ["brouillon", "confirmee", "validee", "en_cours", "livree", "annulee"],
  facture: ["brouillon", "emis", "envoyee", "payee", "en_retard", "annulee"],
  avoir: ["brouillon", "emis", "applique"],
};
const STATUS_LABELS = { brouillon: "Brouillon", envoye: "Envoyé", envoyee: "Envoyée", accepte: "Accepté", refuse: "Refusé", expire: "Expiré", resilie: "Résilié", actif: "Actif", confirmee: "Confirmée", en_cours: "En cours", livree: "Livrée", annulee: "Annulée", payee: "Payée", en_retard: "En retard", emis: "Émis", applique: "Appliqué", "acompté": "Acompté", "soldé": "Soldé", validee: "Validée", archivee: "Archivée" };
const DOC_TITLE_MAP = {
  devis: { new: "Nouveau devis", edit: "Modifier le devis" },
  proforma: { new: "Nouveau devis", edit: "Modifier le devis" },
  commande: { new: "Nouvelle commande", edit: "Modifier la commande" },
  facture: { new: "Nouvelle facture", edit: "Modifier la facture" },
  avoir: { new: "Nouvel avoir", edit: "Modifier l’avoir" },
  contrat: { new: "Nouveau contrat", edit: "Modifier le contrat" },
};
const PAYMENT_LABELS = { virement: "Virement", cheque: "Chèque", carte: "Carte", especes: "Espèces", prélèvement: "Prélèvement", litige: "Litige", avoir: "Avoir", traite: "Traite", autre: "Autre" };
const DEFAULT_PAYMENT_METHODS = [
  { value: "virement", label: "Virement" },
  { value: "cheque", label: "Chèque" },
  { value: "carte", label: "Carte" },
  { value: "especes", label: "Espèces" },
  { value: "prelevement", label: "Prélèvement" },
  { value: "traite", label: "Traite" },
  { value: "autre", label: "Autre" },
];
const DEFAULT_PAYMENT_TERMS = [
  { value: "a_commande", label: "À la commande" },
  { value: "a_reception", label: "À réception de facture" },
  { value: "a_reception_marchandise", label: "À réception de marchandise" },
  { value: "au_depart", label: "Au départ marchandise" },
  { value: "a_livraison", label: "À la livraison" },
  { value: "15j", label: "15 jours" },
  { value: "30j", label: "30 jours" },
  { value: "30j_fdm", label: "30 jours fin de mois" },
  { value: "45j", label: "45 jours" },
  { value: "45j_fdm", label: "45 jours fin de mois" },
  { value: "60j", label: "60 jours" },
];
const TYPE_LABEL_MAP = { devis: "Devis", proforma: "Proforma", commande: "Commande", facture: "Facture", avoir: "Avoir" };

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

function AttachedPdfSection({ form, setForm, editing }) {
  const hasNew = !!form.attached_pdf_base64;
  const hasExisting = !hasNew && (form.has_attached_pdf || form.attached_pdf_name);
  const fileName = hasNew
    ? form.attached_pdf_name
    : (hasExisting ? (form.attached_pdf_name || "document.pdf") : "");

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Format non supporté (PDF uniquement)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || "").split(",")[1] || "";
      setForm((p) => ({
        ...p,
        attached_pdf_base64: base64,
        attached_pdf_name: file.name,
        attached_pdf_mime: "application/pdf",
        has_attached_pdf: true,
      }));
    };
    reader.readAsDataURL(file);
  };

  const remove = () => {
    setForm((p) => ({
      ...p,
      attached_pdf_base64: "",
      attached_pdf_name: "",
      attached_pdf_mime: "",
      has_attached_pdf: false,
    }));
  };

  const viewExisting = () => {
    if (editing?.id) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${editing.id}/attached-pdf`, "_blank");
    }
  };

  return (
    <div className="rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 px-3 py-2.5" data-testid="attached-pdf-section">
      <div className="flex items-center gap-2 mb-1.5">
        <Paperclip size={13} className="text-indigo-600" />
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700 m-0">Devis ou bon de commande signé par le client (optionnel)</Label>
        <span className="text-[10px] text-slate-500">{"\u00A0— ex\u00A0: devis original envoyé au client"}</span>
      </div>
      {hasNew || hasExisting ? (
        <div className="flex items-center gap-2 text-sm bg-white border border-indigo-200 rounded px-2 py-1.5">
          <FileText size={14} className="text-indigo-600 shrink-0" />
          {hasExisting && editing?.id ? (
            <button type="button" onClick={viewExisting} className="text-indigo-700 hover:text-indigo-900 underline truncate text-left flex-1" data-testid="attached-pdf-view-btn">
              {fileName}
            </button>
          ) : (
            <span className="flex-1 truncate text-slate-700" title={fileName}>{fileName}</span>
          )}
          <button type="button" onClick={remove} className="text-slate-400 hover:text-red-500" data-testid="attached-pdf-remove-btn">
            <X size={14} />
          </button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 h-9 cursor-pointer rounded-md bg-white border border-indigo-200 text-xs text-indigo-700 hover:bg-indigo-50" data-testid="attached-pdf-pick-btn">
          <Paperclip size={12} />
          <span>Joindre un PDF (max 10 Mo)</span>
          <input type="file" accept="application/pdf" className="hidden" onChange={onPick} />
        </label>
      )}
    </div>
  );
}

function SourceChain({ chain, current }) {
  if (!chain?.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-3" data-testid="doc-source-chain">
      <span className="text-slate-500 font-medium">Historique :</span>
      {chain.map((ref, i) => (
        <span key={ref.number || `chain-${i}`} className="inline-flex items-center gap-1">
          <span className="bg-white border border-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-700">{ref.number}</span>
          <span className="text-slate-400">{TYPE_LABEL_MAP[ref.type] || ref.type}{ref.date ? ` (${new Date(ref.date).toLocaleDateString("fr-FR")})` : ""}</span>
          {i < chain.length - 1 && <span className="text-slate-300">&rarr;</span>}
        </span>
      ))}
      <span className="text-slate-300">&rarr;</span>
      <span className="inline-flex items-center gap-1">
        <span className="bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 font-mono text-indigo-700 font-medium">{current.number}</span>
        <span className="text-indigo-500">{TYPE_LABEL_MAP[current.type] || current.type}</span>
      </span>
    </div>
  );
}

function CollapsibleSection({ id, title, summary, open, onToggle, hasError, children }) {
  return (
    <div
      className={`border rounded-lg bg-white overflow-hidden ${hasError ? "border-red-300" : "border-slate-200"}`}
      data-testid={`section-${id}`}
      data-open={open ? "1" : "0"}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        data-testid={`section-${id}-toggle`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight
            size={16}
            className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-90" : ""}`}
          />
          <span className="font-semibold text-slate-900">{title}</span>
          {hasError && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 ml-1">
              <AlertCircle size={12} />
              Champ obligatoire manquant
            </span>
          )}
        </div>
        {!open && summary && (
          <span
            className="text-xs text-slate-500 truncate ml-3 max-w-[55%]"
            data-testid={`section-${id}-summary`}
          >
            {summary}
          </span>
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-4">{children}</div>
      )}
    </div>
  );
}

export default function DocumentFormDialog({ open, onClose, onSaved, editing, activeType, clients, products: initialProducts, user, ClientCombobox, onNewClient, defaultClientId = "" }) {
  const [companySettings, setCompanySettings] = useState({});
  const [products, setProducts] = useState(initialProducts || []);

  useEffect(() => { setProducts(initialProducts || []); }, [initialProducts]);

  // Rafraîchir le catalogue local après création d’un produit à la volée
  const refreshProducts = useCallback(async () => {
    try {
      const r = await import("@/lib/api").then(m => m.productAPI.list({ limit: 5000 }));
      setProducts(r.data?.items || r.data || []);
    } catch { /* silencieux */ }
  }, []);

  const emptyForm = useCallback(() => ({
    type: editing?.type || activeType || "devis", client_id: defaultClientId, date: new Date().toISOString().split("T")[0],
    due_date: "", status: "brouillon", items: [], notes: "", payment_method: "",
    payment_terms: "", deposit_amount: 0, sales_conditions: "", avoir_modality: "", different_delivery_address: false,
    delivery_address: "", delivery_city: "", delivery_postal_code: "", delivery_country: "",
  }), [editing, activeType, defaultClientId]);

  const [form, setForm] = useState(emptyForm);
  const [showPreview, setShowPreview] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);

  // État des sections repliables
  // Règles :
  //  - À la création : "document" ouvert, autres fermés
  //  - À l’édition : tout fermé sauf "lignes"
  //  - Section avec champ obligatoire manquant → forcée ouverte (via hasError)
  const [openSections, setOpenSections] = useState({
    avoir: true, document: true, reglement: false, lignes: false, notes: false,
  });

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Fermer l’aperçu avec Échap
  useEffect(() => {
    if (!showPreview) return undefined;
    const handler = (e) => { if (e.key === "Escape") setShowPreview(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showPreview]);

  useEffect(() => {
    adminAPI.getSettings().then(r => setCompanySettings(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setValidationAttempted(false);
    if (editing) {
      // Filtrer les champs obsolètes lors du rechargement d’un ancien document
      const { is_recurring: _ir, recurrence_period: _rp, ...cleanEditing } = editing;
      setForm(cleanEditing);
      setOpenSections({ avoir: true, document: false, reglement: false, lignes: true, notes: false });
    } else {
      const base = emptyForm();
      if (companySettings.default_payment_terms) base.payment_terms = companySettings.default_payment_terms;
      if (companySettings.default_payment_method) base.payment_method = companySettings.default_payment_method;
      if (companySettings.sales_conditions) base.sales_conditions = companySettings.sales_conditions;
      if (base.type === "avoir") {
        base.avoir_modality_type = companySettings.default_avoir_modality || "";
        base.avoir_modality = defaultAvoirModality(companySettings, base.source_doc_number);
      }
      setForm(base);
      setOpenSections({ avoir: true, document: true, reglement: false, lignes: false, notes: false });
    }
  }, [open, editing, emptyForm, companySettings]);

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { _key: crypto.randomUUID(), product_id: "", product_name: "", description: "", quantity: 1, unit_price: 0, tva_rate: 20, size: "", color: "" }] }));
  const updateItem = (idx, field, val) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      if (field === "product_id") {
        const p = products.find(pr => pr.id === val);
        if (p) {
          items[idx].product_name = p.name;
          if (p.description) items[idx].description = p.description;
          items[idx].unit_price = p.unit_price;
          items[idx].tva_rate = p.tva_rate;
          items[idx].reference = p.reference || "";
        }
      }
      return { ...prev, items };
    });
  };
  const removeItem = (idx) => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  const duplicateItem = (idx) => setForm(prev => {
    const items = [...prev.items];
    const src = items[idx] || {};
    items.splice(idx + 1, 0, { ...src, _key: crypto.randomUUID() });
    return { ...prev, items };
  });

  const totalHT = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const totalTVA = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0) * (i.tva_rate || 0) / 100, 0);
  const totalTTC = totalHT + totalTVA;
  const depositAmount = parseFloat(form.deposit_amount) || 0;
  const netToPay = totalTTC - depositAmount;
  // Un acompte égal au total est autorisé (paiement intégral à la commande) ; au-delà, refus.
  const depositExceedsTotal = depositAmount > Math.round(totalTTC * 100) / 100 + 0.005;

  const save = async () => {
    if (!form.client_id) {
      setValidationAttempted(true);
      toast.error("Client requis");
      setOpenSections(prev => ({ ...prev, document: true }));
      return;
    }
    if (depositExceedsTotal) {
      setValidationAttempted(true);
      toast.error(`L'acompte ne peut pas dépasser le total TTC de ${formatEur(totalTTC)}`);
      setOpenSections(prev => ({ ...prev, reglement: true }));
      return;
    }
    try {
      if (editing) { await documentAPI.update(editing.id, form); toast.success("Document modifié"); }
      else { await documentAPI.create(form); toast.success("Document créé"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const currentDocType = editing?.type || activeType;
  const isSolde = editing?.subtype === "solde";
  const isAcompte = editing?.subtype === "acompte";
  // Verrouillage fiscal du numéro : dès qu'un document est émis, son numéro
  // ne doit plus être modifiable (art. 242 nonies A ann. II CGI — numérotation
  // chronologique, continue, sans doublon). Reste modifiable en brouillon.
  const EMITTED_STATUSES = new Set(["envoye", "envoyee", "payee"]);
  const numberLocked = !!editing && (EMITTED_STATUSES.has(form.status) || form.is_sent === true);
  // Une facture / un avoir ne sort du brouillon que par le bouton « Émettre » (numérotation
  // légale) et n'y revient jamais : le sélecteur de statut est bridé en conséquence.
  const isFiscalType = ["facture", "avoir"].includes(currentDocType);
  const isFiscalDraft = isFiscalType && (form.status || "brouillon") === "brouillon";
  const statusOptions = (() => {
    const all = STATUS_MAP[currentDocType] || ["brouillon"];
    if (!isFiscalType) return all;
    return isFiscalDraft ? ["brouillon"] : all.filter(s => s !== "brouillon");
  })();
  const baseTitle = editing
    ? `${DOC_TITLE_MAP[currentDocType]?.edit || "Modifier"} ${editing.number || ""}`.trim()
    : (DOC_TITLE_MAP[currentDocType]?.new || "Nouveau");
  const dialogTitle = isSolde
    ? `${baseTitle} — facture de solde`
    : isAcompte
      ? `${baseTitle} — facture d’acompte`
      : baseTitle;
  const showDeposit = ["devis", "proforma", "commande"].includes(currentDocType);
  const showDeliveryAddress = ["devis", "proforma", "commande", "facture"].includes(currentDocType);
  const showAttachedPdf = ["devis", "commande", "proforma"].includes(currentDocType);
  // Un avoir ne porte ni échéance, ni mode de règlement, ni conditions de paiement, ni
  // conditions de vente, ni acompte, ni adresse de livraison : le PDF les ignore, le
  // formulaire ne les propose donc plus. La modalité de l'avoir dit ce que devient la somme due.
  const isAvoir = currentDocType === "avoir";
  const avoirModalityMissing = isAvoir && !(form.avoir_modality || "").trim();
  const paymentTermsOptions = (companySettings.payment_terms_options?.length > 0) ? companySettings.payment_terms_options : DEFAULT_PAYMENT_TERMS;
  const paymentMethodOptions = (companySettings.payment_method_options?.length > 0) ? companySettings.payment_method_options : DEFAULT_PAYMENT_METHODS;
  // Affichage tel quel : une valeur stockée absente de la liste reste sélectionnable/visible
  const paymentMethodOptionsWithCurrent = (form.payment_method && !paymentMethodOptions.some(m => m.value === form.payment_method))
    ? [...paymentMethodOptions, { value: form.payment_method, label: form.payment_method }]
    : paymentMethodOptions;

  // ─────────── Résumés + erreurs par section ───────────
  const clientLabel = useMemo(() => {
    const c = clients?.find(cl => cl.id === form.client_id);
    if (!c) return "";
    return c.company || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.trade_name || "";
  }, [clients, form.client_id]);

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "";
  const documentSummary = form.client_id
    ? [clientLabel || "Client", fmtDate(form.date), STATUS_LABELS[form.status] || form.status].filter(Boolean).join(" · ")
    : "Client à sélectionner";
  const documentHasError = validationAttempted && !form.client_id;

  const paymentMethodLabel = paymentMethodOptionsWithCurrent.find(m => m.value === form.payment_method)?.label || form.payment_method || "";
  const paymentTermLabel = paymentTermsOptions.find(pt => pt.value === form.payment_terms)?.label || "";
  const reglementSummary = [
    paymentMethodLabel,
    paymentTermLabel,
    depositAmount > 0 ? `Acompte ${formatEur(depositAmount)}` : "",
  ].filter(Boolean).join(" · ") || "À définir";

  const lignesSummary = form.items.length > 0
    ? `${form.items.length} ligne${form.items.length > 1 ? "s" : ""} · ${formatEur(totalTTC)} TTC`
    : "Aucune ligne";

  const notesText = (form.notes || "").trim();
  const conditionsText = (form.sales_conditions || "").trim();
  const notesSummary = (() => {
    if (!notesText && !conditionsText) return "—";
    const first = notesText || conditionsText;
    return first.length > 60 ? `${first.slice(0, 60)}…` : first;
  })();

  // Section forcée ouverte si erreur
  const isSectionOpen = (key) => openSections[key]
    || (key === "document" && documentHasError)
    || (key === "avoir" && avoirModalityMissing);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] p-0 flex flex-col" data-testid="doc-form-dialog">
        <div className="overflow-y-auto p-6 pb-6" ref={(el) => { if (el) el.dataset.scrollContainer = "1"; }}>
          <DialogHeader><DialogTitle data-testid="doc-dialog-title">{dialogTitle}</DialogTitle></DialogHeader>
          <SourceChain chain={editing?.source_chain} current={editing} />
          {currentDocType === "avoir" && (
            <div className="mt-2">
              <AvoirDraftBanner doc={{ ...form, id: editing?.id, type: currentDocType }} />
            </div>
          )}

          <div className="space-y-3 mt-4">
            {/* ─────────── Section Avoir : la modalité d'abord, elle conditionne l'émission ─────────── */}
            {isAvoir && (
              <CollapsibleSection
                id="avoir"
                title="Avoir"
                summary={(form.avoir_modality || "").trim() || "Modalité à préciser"}
                open={isSectionOpen("avoir")}
                onToggle={() => toggleSection("avoir")}
                hasError={avoirModalityMissing}
              >
                <AvoirModalityField form={form} setForm={setForm} />
              </CollapsibleSection>
            )}
            {/* ─────────── Section 1 : Document ─────────── */}
            <CollapsibleSection
              id="document"
              title="Document"
              summary={documentSummary}
              open={isSectionOpen("document")}
              onToggle={() => toggleSection("document")}
              hasError={documentHasError}
            >
              {editing && user?.is_superadmin && (
                <div>
                  <Label>Numéro de document</Label>
                  {numberLocked ? (
                    <>
                      <Input
                        value={form.number || ""}
                        readOnly
                        disabled
                        className="font-mono mt-1 bg-slate-50 text-slate-700 cursor-not-allowed"
                        data-testid="doc-number-input"
                      />
                      <p className="text-[11px] text-slate-500 mt-1 italic" data-testid="doc-number-locked-hint">
                        Numéro figé&nbsp;— document émis
                      </p>
                    </>
                  ) : (
                    <Input
                      value={form.number || ""}
                      onChange={e => setForm(prev => ({...prev, number: e.target.value}))}
                      className="font-mono mt-1"
                      data-testid="doc-number-input"
                    />
                  )}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Client</Label>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-indigo-600" onClick={onNewClient} data-testid="doc-new-client-btn">+ Nouveau</Button>
                </div>
                <ClientCombobox clients={clients} value={form.client_id} onChange={v => setForm(prev => ({...prev, client_id: v}))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label>Date</Label><DateField value={form.date} onChange={e => setForm(prev => ({...prev, date: e.target.value}))} data-testid="doc-date" /></div>
                {currentDocType !== "avoir" && (
                  <div>
                    <Label data-testid="doc-due-date-label">
                      {currentDocType === "devis" || currentDocType === "proforma"
                        ? "Validité"
                        : currentDocType === "commande"
                          ? "Date de livraison"
                          : "Échéance"}
                    </Label>
                    <DateField value={form.due_date} onChange={e => setForm(prev => ({...prev, due_date: e.target.value}))} data-testid="doc-due-date" />
                  </div>
                )}
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={v => setForm(prev => ({...prev, status: v}))} disabled={isFiscalDraft}>
                    <SelectTrigger data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{statusOptions.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}</SelectContent>
                  </Select>
                  {isFiscalDraft && (
                    <p className="text-[11px] text-slate-500 mt-1" data-testid="doc-status-draft-hint">
                      Statut figé en brouillon : utilisez « Émettre » pour attribuer le numéro définitif.
                    </p>
                  )}
                  {currentDocType === "facture" && editing && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="doc-is-sent-toggle">
                      <input type="checkbox" className="rounded border-slate-300" checked={!!form.is_sent} onChange={e => setForm(prev => ({...prev, is_sent: e.target.checked}))} />
                      <span className="text-xs text-slate-600">Envoyée (hors logiciel)</span>
                    </label>
                  )}
                </div>
              </div>
              {showDeliveryAddress && <DeliveryAddressSection form={form} setForm={setForm} />}
              {showAttachedPdf && <AttachedPdfSection form={form} setForm={setForm} editing={editing} />}
            </CollapsibleSection>

            {/* ─────────── Section 2 : Règlement (sans objet sur un avoir) ─────────── */}
            {!isAvoir && (
            <CollapsibleSection
              id="reglement"
              title="Règlement"
              summary={reglementSummary}
              open={isSectionOpen("reglement")}
              onToggle={() => toggleSection("reglement")}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Mode de règlement</Label>
                  <Select value={form.payment_method} onValueChange={v => setForm(prev => ({...prev, payment_method: v}))}>
                    <SelectTrigger data-testid="doc-payment-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{paymentMethodOptionsWithCurrent.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conditions de paiement</Label>
                  <Select value={form.payment_terms || ""} onValueChange={v => setForm(prev => ({...prev, payment_terms: v}))}>
                    <SelectTrigger data-testid="doc-payment-terms"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{paymentTermsOptions.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {showDeposit && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Acompte à verser (€)</Label>
                    <NumberField value={form.deposit_amount || ""} onValueChange={v => setForm(prev => ({...prev, deposit_amount: typeof v === "number" ? v : 0}))} placeholder="0,00" testid="doc-deposit-amount" />
                    {depositExceedsTotal && (
                      <p className="mt-1 text-xs font-medium text-red-600" data-testid="doc-deposit-error">
                        L'acompte ne peut pas dépasser le total TTC de {formatEur(totalTTC)}
                      </p>
                    )}
                  </div>
                  {depositAmount > 0 && !depositExceedsTotal && (
                    <div className="flex items-end">
                      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 text-sm">
                        <span className="text-amber-700">Net à payer : </span>
                        <span className="font-bold text-amber-900">{formatEur(netToPay)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {editing && currentDocType === "facture" && <PaymentDetailsSection payments={editing.payments} />}
            </CollapsibleSection>
            )}

            {/* ─────────── Section 3 : Lignes ─────────── */}
            <CollapsibleSection
              id="lignes"
              title="Lignes"
              summary={lignesSummary}
              open={isSectionOpen("lignes")}
              onToggle={() => toggleSection("lignes")}
            >
              <DocumentItemsEditor
                items={form.items}
                products={products}
                onAdd={addItem}
                onUpdate={updateItem}
                onRemove={removeItem}
                onDuplicate={duplicateItem}
                onProductCreated={refreshProducts}
                showPreview={showPreview}
                onTogglePreview={() => setShowPreview(v => !v)}
              />
              {showPreview && (
                <div className="mt-3" data-testid="live-preview-inline">
                  <LiveDocumentLinesPreview items={form.items} products={products} />
                </div>
              )}
              <DocumentTotals totalHT={totalHT} totalTVA={totalTVA} totalTTC={totalTTC} depositAmount={depositAmount} netToPay={netToPay} acompteDeductions={form.acompte_deductions} payments={form.payments} />
            </CollapsibleSection>

            {/* ─────────── Section 4 : Notes et conditions ─────────── */}
            <CollapsibleSection
              id="notes"
              title="Notes et conditions"
              summary={notesSummary}
              open={isSectionOpen("notes")}
              onToggle={() => toggleSection("notes")}
            >
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm(prev => ({...prev, notes: e.target.value}))} rows={2} data-testid="doc-notes" />
              </div>
              {currentDocType !== "avoir" && (
                <div>
                  <Label>Conditions de vente</Label>
                  <Textarea value={form.sales_conditions || ""} onChange={e => setForm(prev => ({...prev, sales_conditions: e.target.value}))} rows={3} placeholder="Conditions générales de vente…" data-testid="doc-sales-conditions" />
                </div>
              )}
            </CollapsibleSection>

            <Button onClick={save} className="hidden" data-testid="doc-save-btn-hidden">Enregistrer</Button>
          </div>
        </div>
        {/* Footer fixe : Annuler + Enregistrer avec respiration */}
        <div className="border-t bg-white px-6 py-3 flex items-center justify-end gap-3 shrink-0" data-testid="doc-form-footer">
          <Button variant="outline" onClick={onClose} data-testid="doc-cancel-btn">Annuler</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="doc-save-btn">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
